import React, { useState, useEffect } from "react";
import useApiEp from "../../hooks/useApiEp";
import { Alert, Container, Box, Button, CircularProgress } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import DownloadIcon from "@mui/icons-material/Download";
import PageTitle from "../../widgets/PageTitle";
import DataTable from "../../widgets/DataTable";
import {
  getErrorResponse,
  getHeader,
  roundToMaxDecimals,
} from "../../utils/util";
import CreditStats from "./CreditStats";
import * as XLSX from "xlsx";
import axios from "axios"; // or your existing fetch util
import { API_ENDPOINT } from "../../utils/constants";

const CreditHistory = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(30);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("recharge");
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, isError, error } = useApiEp(
    `/credit/get-recharge-history?page=${page}&limit=${limit}&customerId=${selectedCustomer}&type=${selectedTransaction}&rechargeType=${selectedType}&category=${selectedCategory}`
  );

  const {
    data: customerIds,
    isLoading: isCLoading,
    isError: isCerror,
    error: cError,
  } = useApiEp(`/credit/customer-ids`);

  const {
    data: rTypes,
    isLoading: isRtypeLoading,
    isError: isRerror,
    error: rError,
  } = useApiEp(`/credit/get-unique-recharge-types`);

  const TRANSACTIONS = ["credit", "debit"];
  const CATEGORIES = ["usage", "recharge"];

  useEffect(() => {
    setPage(1);
  }, [selectedCustomer, selectedType, selectedTransaction, selectedCategory]);

  const exportToExcel = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams({
        ...(selectedCustomer && { customerId: selectedCustomer }),
        ...(selectedTransaction && { type: selectedTransaction }),
        ...(selectedType && { rechargeType: selectedType }),
        ...(selectedCategory && { category: selectedCategory }),
      });

      const res = await axios.get(
        `${API_ENDPOINT}/credit/export-recharge-history?${params.toString()}`,
        getHeader()
      );
      const rows = res.data;

      const sheetData = rows.map((row, idx) => ({
        "S.No.": idx + 1,
        Type: row.type === "credit" ? "Credit" : "Debit",
        "Customer ID": row.customerId,
        Previous: roundToMaxDecimals(row.prevous, 2),
        Amount: roundToMaxDecimals(row.amount, 2),
        "Recharge Type": row.rechargeType ?? "",
        Remarks: row.remarks ?? "",
        Date: new Date(row.updatedOn).toLocaleString("en-IN", {
          dateStyle: "medium",
        }),
      }));

      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Credit History");

      const fileName = `credit_history_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Container sx={{ mt: 3 }}>
      <CreditStats />
      <PageTitle my={2}>Credit History</PageTitle>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Autocomplete
          options={customerIds ?? []}
          value={selectedCustomer || null}
          onChange={(event, newValue) => setSelectedCustomer(newValue || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Customer ID"
              size="small"
              sx={{ minWidth: 200 }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isCLoading ? <div>Loading...</div> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option === value}
          clearOnBlur={false}
          handleHomeEndKeys
          disabled={isCerror}
        />

        <Autocomplete
          options={rTypes ?? []}
          value={selectedType || null}
          onChange={(event, newValue) => setSelectedType(newValue || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Recharge Type"
              size="small"
              sx={{ minWidth: 200 }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isRtypeLoading ? <div>Loading...</div> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option === value}
          clearOnBlur={false}
          handleHomeEndKeys
          disabled={isRerror}
        />

        <Autocomplete
          options={["", ...TRANSACTIONS]}
          value={selectedTransaction || ""}
          onChange={(event, newValue) => setSelectedTransaction(newValue || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Transaction Type"
              size="small"
              sx={{ minWidth: 200 }}
            />
          )}
          isOptionEqualToValue={(option, value) => option === value}
          renderOption={(props, option) => (
            <li {...props}>
              {option === "" ? "All" : option === "credit" ? "Credit" : "Debit"}
            </li>
          )}
          clearOnBlur={false}
          handleHomeEndKeys
        />

        <Autocomplete
          options={["", ...CATEGORIES]}
          value={selectedCategory}
          onChange={(event, newValue) => setSelectedCategory(newValue || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Category"
              size="small"
              sx={{ minWidth: 200 }}
            />
          )}
          isOptionEqualToValue={(option, value) => option === value}
          renderOption={(props, option) => (
            <li {...props}>
              {option === ""
                ? "All"
                : option === "usage"
                ? "Usage"
                : "Recharge"}
            </li>
          )}
          clearOnBlur={false}
          handleHomeEndKeys
        />

        {/* Export Button */}
        <Button
          variant="outlined"
          size="small"
          startIcon={
            exporting ? <CircularProgress size={14} /> : <DownloadIcon />
          }
          onClick={exportToExcel}
          disabled={exporting}
          sx={{ ml: "auto", height: 40, whiteSpace: "nowrap" }}
        >
          {exporting ? "Exporting..." : "Export Excel"}
        </Button>
      </Box>

      {isCerror && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load customer IDs: {getErrorResponse(cError)}
        </Alert>
      )}
      {isRerror && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load recharge types: {getErrorResponse(rError)}
        </Alert>
      )}

      {isError ? (
        <Alert severity="error">{getErrorResponse(error)}</Alert>
      ) : (
        <DataTable
          sx={{ my: 1 }}
          columns={[
            {
              id: "serial",
              label: "S.No.",
              render: (_v, _row, idx) => (page - 1) * limit + idx + 1,
            },
            {
              id: "type",
              label: "Type",
              render: (v) => (
                <span style={{ color: v === "credit" ? "green" : "red" }}>
                  {v === "credit" ? "Credit" : "Debit"}
                </span>
              ),
            },
            { id: "customerId", label: "Customer ID" },
            {
              id: "prevous",
              label: "Previous",
              render: (v) => roundToMaxDecimals(v, 2),
            },
            {
              id: "amount",
              label: "Amount",
              render: (v) => roundToMaxDecimals(v, 2),
            },
            { id: "rechargeType", label: "Recharge Type" },
            { id: "remarks", label: "Remarks" },
            {
              id: "updatedOn",
              label: "Date",
              render: (v) =>
                new Date(v).toLocaleString("en-IN", { dateStyle: "medium" }),
            },
          ]}
          actions={[]}
          rows={data?.data ?? []}
          page={page}
          limit={limit}
          totalPages={data?.totalPages ?? 1}
          onPageChange={setPage}
          loading={isLoading}
        />
      )}
    </Container>
  );
};

export default CreditHistory;
